import { ReactElement, ReactNode, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { Spacing } from '../../../../enums/spacing'
import { ButtonDropdown } from '../../../ButtonDropdown'
import { Flex } from '../../../Flex'
import { Icon, IconName } from '../../../Icon'
import { IconButton } from '../../../IconButton'
import { NavItem } from '../../../NavList'
import { Space } from '../../../Space'
import { Text } from '../../../Text'
import { useDrawer } from '../../context/drawerContext'
import * as Styled from './styles'

interface DrawerHeaderProps {
  children?: ReactNode
  closable?: boolean
  closeLabel?: string
  collapseLabel?: string
  expandLabel?: string
  /**
   * Render the expand/collapse toggle. Only meaningful when the Drawer is opened in
   * `Compact`/`Expanded` mode. Defaults to `false`.
   */
  expandable?: boolean
  labelId?: string
  title?: ReactNode
  icon?: IconName
  onBack?: () => void
  actions?: NavItem[]
  onAction?: (id: string, value: string) => void
  customContent?: ReactNode
}

export const DrawerHeader = ({
  actions,
  children,
  closable,
  closeLabel,
  collapseLabel,
  customContent,
  expandable = false,
  expandLabel,
  icon,
  labelId,
  title,
  onBack,
  onAction,
}: DrawerHeaderProps): ReactElement => {
  const { t } = useTranslation()
  const { isExpanded, toggleExpanded, requestClose } = useDrawer()
  const resolvedCloseLabel = closeLabel ?? t('close')
  const resolvedExpandLabel = expandLabel ?? t('expand')
  const resolvedCollapseLabel = collapseLabel ?? t('collapse')

  const handleCloseClick = useCallback(() => {
    if (closable) {
      requestClose()
    }
  }, [closable, requestClose])

  /* You can ship fully customised header instead of the default structure with icon-title-actions-close */
  if (customContent) {
    return (
      <Styled.CustomDrawerHeaderWrapper>
        {/* Close button will be placed on the top-right corner */}
        {closable && (
          <Styled.CustomHeaderCloseButton>
            <IconButton icon="x" aria-label={resolvedCloseLabel} onClick={handleCloseClick} />
          </Styled.CustomHeaderCloseButton>
        )}
        {customContent}
      </Styled.CustomDrawerHeaderWrapper>
    )
  }

  return (
    <Styled.DrawerHeaderWrapper>
      {onBack && (
        <>
          <IconButton icon="arrowLeft" onClick={onBack} />
          <Space size="s24" horizontal />
        </>
      )}
      <Flex alignItems="center" gap={Spacing.S16}>
        {icon && <Icon color="currentColor" icon={icon} />}
        {title && (
          <Text id={labelId} variant="h4">
            {title}
          </Text>
        )}
      </Flex>
      {children}
      <Styled.HeaderRightSection>
        {actions && actions.length > 0 && (
          <ButtonDropdown
            items={actions}
            onSelect={onAction}
            placement="bottom-end"
            triggerElement={<IconButton icon="ellipsis" />}
          />
        )}
        {expandable && (
          <IconButton
            aria-label={isExpanded ? resolvedCollapseLabel : resolvedExpandLabel}
            icon={isExpanded ? 'minimize' : 'maximize'}
            onClick={toggleExpanded}
          />
        )}
        {closable && <IconButton icon="x" aria-label={resolvedCloseLabel} onClick={handleCloseClick} />}
      </Styled.HeaderRightSection>
    </Styled.DrawerHeaderWrapper>
  )
}
